import { useEffect, useState } from 'react'
import { format } from 'date-fns'
import { DatePicker } from 'components/date-picker'
import { Button } from 'components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'components/ui/table'
import {
  AGGREGATION_LABELS,
  GRANULARITIES,
  PERIODS,
  type ReportFilters,
  changePct,
  formatMetric,
  formatPeriod,
  formatRowDate,
  presetDates,
  toQuery,
} from 'helpers/daily-report'
import { Loader } from 'lucide-react'
import { fetchDailyReport } from 'store/dailyReport/dailyReportSlice'
import { useAppDispatch, useAppSelector } from 'store/store'
import { MetricTile } from './MetricTile'
import { MetricTrendChart } from './MetricTrendChart'

type Filters = Omit<ReportFilters, 'source'>

const initialFilters: Filters = { period: 'last_30_days', granularity: 'day' }

/** Every metric of one source: tiles, trend of the chosen one, table. */
export const DailyReportExplorer = ({
  source,
  onSourceChange,
}: {
  source: ReportFilters['source']
  onSourceChange: (source: ReportFilters['source']) => void
}) => {
  const dispatch = useAppDispatch()
  const { sources, report, loading, error } = useAppSelector(
    (state) => state.dailyReport
  )
  const [filters, setFilters] = useState<Filters>(initialFilters)
  const [selectedKey, setSelectedKey] = useState<string | null>(null)

  useEffect(() => {
    // A custom period waits for both dates
    if (filters.period === 'custom' && (!filters.dateFrom || !filters.dateTo))
      return
    dispatch(fetchDailyReport(toQuery({ ...filters, source })))
  }, [dispatch, filters, source])

  // Ignore the previous source's report while the new one loads
  const current = report?.source === source ? report : null
  const metrics = current?.metrics ?? []
  // The chart follows the chosen metric while it exists in the current source
  const selected =
    metrics.find((m) => m.key === selectedKey) ?? metrics[0] ?? null
  const granularity = current?.period.granularity ?? filters.granularity
  const previousTitle = current
    ? `Rispetto a ${formatPeriod(current.previous_period.from, current.previous_period.to)}`
    : undefined

  return (
    <>
      <div className='flex flex-wrap items-center gap-2 rounded-lg border bg-muted/40 p-3'>
        <Select
          value={source}
          onValueChange={(value) =>
            onSourceChange(value as ReportFilters['source'])
          }
        >
          <SelectTrigger className='w-56 bg-background'>
            <SelectValue placeholder='Fonte' />
          </SelectTrigger>
          <SelectContent>
            {sources.map((s) => (
              <SelectItem key={s.key} value={s.key}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className='flex flex-wrap gap-1'>
          {PERIODS.map((period) => (
            <Button
              key={period.key}
              size='sm'
              variant={filters.period === period.key ? 'secondary' : 'ghost'}
              onClick={() => setFilters({ ...filters, period: period.key })}
            >
              {period.label}
            </Button>
          ))}
          <Button
            size='sm'
            variant={filters.period === 'custom' ? 'secondary' : 'ghost'}
            onClick={() => {
              const { from, to } = presetDates(filters.period)
              setFilters({
                ...filters,
                period: 'custom',
                dateFrom: filters.dateFrom ?? from,
                dateTo: filters.dateTo ?? to,
              })
            }}
          >
            Su misura
          </Button>
        </div>

        {filters.period === 'custom' && (
          <>
            <DatePicker
              selected={filters.dateFrom}
              onSelect={(date) => setFilters({ ...filters, dateFrom: date })}
              placeholder='Dal'
            />
            <DatePicker
              selected={filters.dateTo}
              onSelect={(date) => setFilters({ ...filters, dateTo: date })}
              placeholder='Al'
            />
          </>
        )}

        <div className='ms-auto flex overflow-hidden rounded-md border'>
          {GRANULARITIES.map((g) => (
            <button
              key={g.key}
              type='button'
              aria-pressed={filters.granularity === g.key}
              onClick={() => setFilters({ ...filters, granularity: g.key })}
              className={`h-8 px-3 text-sm not-first:border-s ${
                filters.granularity === g.key
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-background text-muted-foreground hover:bg-accent'
              }`}
            >
              {g.label}
            </button>
          ))}
        </div>
      </div>

      {current && !loading && (
        <p className='mt-3 text-sm text-muted-foreground'>
          {current.days_with_data} giorni con dati
          {current.missing_days > 0 &&
            ` · ${current.missing_days} giorni mancanti`}
          {current.last_collected_at &&
            ` · ultimo aggiornamento ${format(
              new Date(current.last_collected_at),
              'dd/MM/yyyy HH:mm'
            )}`}
        </p>
      )}

      {loading ? (
        <Loader className='mt-6 h-5 w-5 animate-spin' />
      ) : error ? (
        <p className='mt-6 text-red-500'>Errore: {error}</p>
      ) : !current || current.rows.length === 0 ? (
        <p className='mt-6 text-muted-foreground'>
          Nessun dato nel periodo selezionato.
        </p>
      ) : (
        <>
          <div className='mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5'>
            {metrics.map((metric) => (
              <MetricTile
                key={metric.key}
                label={metric.label}
                value={formatMetric(metric, current.summary[metric.key])}
                note={AGGREGATION_LABELS[metric.aggregation]}
                change={changePct(
                  current.summary[metric.key],
                  current.previous_summary[metric.key]
                )}
                changeTitle={previousTitle}
                title={metric.description ?? undefined}
                selected={metric.key === selected?.key}
                onSelect={() => setSelectedKey(metric.key)}
              />
            ))}
          </div>

          {selected && (
            <div className='mt-4 rounded-lg border p-4'>
              <h3 className='text-sm font-semibold'>{selected.label}</h3>
              <p className='text-xs text-muted-foreground'>
                {granularity === 'day'
                  ? 'Valore per giorno'
                  : `Per ${granularity === 'week' ? 'settimana' : 'mese'}: ${
                      AGGREGATION_LABELS[selected.aggregation]
                    } dei giorni`}
              </p>
              <div className='mt-3'>
                <MetricTrendChart
                  rows={current.rows}
                  metric={selected}
                  granularity={granularity}
                />
              </div>
            </div>
          )}

          <div className='mt-4 overflow-x-auto rounded-lg border'>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className='sticky left-0 bg-background'>
                    Data
                  </TableHead>
                  {metrics.map((metric) => (
                    <TableHead
                      key={metric.key}
                      className='text-right whitespace-nowrap'
                    >
                      {metric.label}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow className='bg-muted/40 font-semibold'>
                  <TableCell className='sticky left-0 bg-muted'>
                    Periodo
                  </TableCell>
                  {metrics.map((metric) => (
                    <TableCell
                      key={metric.key}
                      className='text-right tabular-nums'
                    >
                      {formatMetric(metric, current.summary[metric.key])}
                    </TableCell>
                  ))}
                </TableRow>
                {[...current.rows].reverse().map((row) => (
                  <TableRow key={row.date}>
                    <TableCell className='sticky left-0 bg-background whitespace-nowrap'>
                      {formatRowDate(row.date, granularity)}
                    </TableCell>
                    {metrics.map((metric) => (
                      <TableCell
                        key={metric.key}
                        className='text-right tabular-nums'
                      >
                        {formatMetric(metric, row.values[metric.key])}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </>
  )
}
